export const DEFAULT_IMPOSITION_OUTPUT_VARIABLES = {
  sheets: 'pliegos',
  motherSheets: 'hojamadre',
  spoilage: 'desperdicio',
  sheetWidth: 'anchohoja',
  sheetHeight: 'largohoja',
  pagesPerSheet: 'paginasporpliego',
};

export const IMPOSITION_OUTPUT_VARIABLE_DEFINITIONS = [
  {
    key: DEFAULT_IMPOSITION_OUTPUT_VARIABLES.sheets,
    label: 'Pliegos',
    unit: 'pliegos',
  },
  {
    key: DEFAULT_IMPOSITION_OUTPUT_VARIABLES.motherSheets,
    label: 'Hojas madre',
    unit: 'hojas',
  },
  {
    key: DEFAULT_IMPOSITION_OUTPUT_VARIABLES.spoilage,
    label: 'Desperdicio',
    unit: 'hojas',
  },
  {
    key: DEFAULT_IMPOSITION_OUTPUT_VARIABLES.sheetWidth,
    label: 'Ancho hoja',
    unit: 'cm',
  },
  {
    key: DEFAULT_IMPOSITION_OUTPUT_VARIABLES.sheetHeight,
    label: 'Largo hoja',
    unit: 'cm',
  },
  {
    key: DEFAULT_IMPOSITION_OUTPUT_VARIABLES.pagesPerSheet,
    label: 'Paginas por pliego',
    unit: 'pag.',
  },
];

const OUTPUT_ALIASES = {
  sheets: ['pliegos', 'cantidad_pliegos', 'cantpliegos'],
  motherSheets: ['hojamadre', 'hoja_madre', 'hojasmadre', 'hojas_madre', 'cantidad_hojas_madre'],
  spoilage: ['desperdicio', 'desperdiciohoja', 'desperdicio_hoja', 'desperdicio_hojas', 'merma', 'mermahoja'],
  sheetWidth: ['anchohoja', 'ancho_hoja', 'ancho_pliego', 'anchopliego'],
  sheetHeight: ['largohoja', 'largo_hoja', 'altohoja', 'alto_hoja', 'alto_pliego', 'largopliego'],
  pagesPerSheet: ['paginasporpliego', 'paginas_por_pliego', 'pags_por_pliego', 'items_por_pliego'],
};

function findExistingVariableKey(variables = [], candidates = []) {
  const keysByLowercase = new Map(
    (variables || [])
      .filter(v => v?.key)
      .map(v => [String(v.key).toLowerCase(), v.key])
  );

  for (const candidate of candidates) {
    const found = keysByLowercase.get(candidate.toLowerCase());
    if (found) return found;
  }

  return '';
}

function resolveOutputKey(configValue, defaultValue, aliases, variables) {
  const existingKeys = new Set((variables || []).map(v => v?.key).filter(Boolean));
  if (configValue && existingKeys.has(configValue)) {
    return configValue;
  }

  const matchedAlias = findExistingVariableKey(variables, aliases);
  if (matchedAlias) return matchedAlias;

  return configValue || defaultValue;
}

export function normalizeImpositionConfig(config = {}, variables = []) {
  return {
    ...config,
    sheets_variable: resolveOutputKey(
      config.sheets_variable,
      DEFAULT_IMPOSITION_OUTPUT_VARIABLES.sheets,
      OUTPUT_ALIASES.sheets,
      variables
    ),
    mother_sheet_variable: resolveOutputKey(
      config.mother_sheet_variable,
      DEFAULT_IMPOSITION_OUTPUT_VARIABLES.motherSheets,
      OUTPUT_ALIASES.motherSheets,
      variables
    ),
    spoilage_variable: resolveOutputKey(
      config.spoilage_variable,
      DEFAULT_IMPOSITION_OUTPUT_VARIABLES.spoilage,
      OUTPUT_ALIASES.spoilage,
      variables
    ),
    spoilage_sheets: Number.isFinite(Number(config.spoilage_sheets ?? config.spoilageSheets))
      ? Number(config.spoilage_sheets ?? config.spoilageSheets)
      : 50,
    sheet_width_variable: resolveOutputKey(
      config.sheet_width_variable,
      DEFAULT_IMPOSITION_OUTPUT_VARIABLES.sheetWidth,
      OUTPUT_ALIASES.sheetWidth,
      variables
    ),
    sheet_height_variable: resolveOutputKey(
      config.sheet_height_variable,
      DEFAULT_IMPOSITION_OUTPUT_VARIABLES.sheetHeight,
      OUTPUT_ALIASES.sheetHeight,
      variables
    ),
    pages_per_sheet_variable: resolveOutputKey(
      config.pages_per_sheet_variable,
      DEFAULT_IMPOSITION_OUTPUT_VARIABLES.pagesPerSheet,
      OUTPUT_ALIASES.pagesPerSheet,
      variables
    ),
  };
}

export function getImpositionOutputKeys(config = {}) {
  const normalized = normalizeImpositionConfig(config);
  return [
    normalized.sheets_variable,
    normalized.mother_sheet_variable,
    normalized.spoilage_variable,
    normalized.sheet_width_variable,
    normalized.sheet_height_variable,
    normalized.pages_per_sheet_variable,
  ].filter(Boolean);
}

export function createImpositionOutputVariable(definition) {
  return {
    key: definition.key,
    label: definition.label,
    type: 'number',
    unit: definition.unit,
    default_value: 0,
    options: [],
    checked_value: 0,
    unchecked_value: 0,
    formula: '',
    show_in_budget: false,
  };
}

function getRuntimeSourceSheet(runtimeConfig = {}) {
  return (
    runtimeConfig.sourceCut?.sourceSheet
    || runtimeConfig.sourceSheet
    || runtimeConfig.source_sheet
    || null
  );
}

export function getImpositionDerivedValues(option, runtimeConfig, productConfig = {}) {
  const normalized = normalizeImpositionConfig(productConfig);
  if (!option || !runtimeConfig) return {};

  const sourceSheet = getRuntimeSourceSheet(runtimeConfig);
  const sourceWidth = Number(sourceSheet?.width) || Number(runtimeConfig.sheetWidth) || 0;
  const sourceHeight = Number(sourceSheet?.height) || Number(runtimeConfig.sheetHeight) || 0;
  const baseSheetsNeeded = Number(option.sheets_needed) || 0;
  const generatedSheetsPerMother = Number(runtimeConfig.sourceCut?.sheetCount) || 1;
  const spoilageMotherSheets = Math.max(
    0,
    Math.ceil(Number(runtimeConfig.spoilageSheets ?? runtimeConfig.spoilage_sheets ?? normalized.spoilage_sheets) || 0)
  );
  const motherSheetsNeeded = Math.ceil(baseSheetsNeeded / Math.max(generatedSheetsPerMother, 1));
  const totalMotherSheets = motherSheetsNeeded + spoilageMotherSheets;
  const totalSheetsNeeded = baseSheetsNeeded + (spoilageMotherSheets * Math.max(generatedSheetsPerMother, 1));
  const derived = {
    [normalized.sheets_variable]: totalSheetsNeeded,
    [normalized.mother_sheet_variable]: totalMotherSheets,
    [normalized.spoilage_variable]: spoilageMotherSheets,
    [normalized.sheet_width_variable]: sourceWidth,
    [normalized.sheet_height_variable]: sourceHeight,
  };

  const pagesPerSheet = Number(option.items_per_sheet);
  if (Number.isFinite(pagesPerSheet) && pagesPerSheet > 0) {
    derived[normalized.pages_per_sheet_variable] = pagesPerSheet;
  }

  return derived;
}

export function applyImpositionDerivedValues(currentValues, option, runtimeConfig, productConfig = {}) {
  if (!option || !runtimeConfig) return currentValues;

  const derived = getImpositionDerivedValues(option, runtimeConfig, productConfig);

  const next = { ...currentValues };
  let changed = false;

  Object.entries(derived).forEach(([key, value]) => {
    if (!key) return;
    if (Number(next[key]) !== value) {
      next[key] = value;
      changed = true;
    }
  });

  return changed ? next : currentValues;
}
